import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Star, Briefcase, UserPlus, Crown } from "lucide-react";
import { api, apiError, formatDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function Ranking() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [myEvents, setMyEvents] = useState([]);
  const [invitee, setInvitee] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/ranking").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/events?scope=mine").then((r) => setMyEvents(r.data.filter((e) => e.status === "aberto"))).catch(() => {});
  }, []);

  const medal = (i) => ["#FFD700", "#C0C0C0", "#CD7F32"][i] || null;

  const invite = async (eventId) => {
    try { await api.post(`/events/${eventId}/invite`, { user_id: invitee.id }); toast.success(`${invitee.stage_name} convidado!`); setInvitee(null); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const restricted = data?.restricted;
  const users = restricted ? [] : (data?.ranking || []);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="ranking-page">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3"><Trophy className="h-7 w-7 text-[#FFD700]" />Ranking</h1>
        <p className="text-sm text-muted-foreground mt-1">Reputação por avaliações, trabalhos concluídos e presença (check-ins).</p>
      </div>

      {restricted ? (
        <div className="space-y-4" data-testid="ranking-restricted">
          <div className="bg-[#121217] border border-border rounded-sm p-6 text-center">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Sua posição no Ranking Geral</div>
            <div className="mt-2 text-4xl font-bold mono text-[#FFD700]" data-testid="ranking-my-position">
              {data?.position ? `#${data.position}` : "—"}
            </div>
            <p className="text-sm text-zinc-400 mt-1" data-testid="ranking-position-text">
              {data?.position ? <>Você está na posição <b>{data.position}</b> entre <b>{data.total}</b> profissionais.</> : `Entre ${data?.total || 0} profissionais.`}
            </p>
            {data?.me && (
              <div className="flex items-center justify-center gap-4 text-sm mt-3">
                <span className="flex items-center gap-1 text-amber-400"><Star className="h-4 w-4 fill-amber-400" />{data.me.rating.avg || "—"}</span>
                <span className="flex items-center gap-1 text-zinc-400"><Briefcase className="h-4 w-4" />{data.me.gigs_done}</span>
                <span className="mono text-[#00F0FF] font-semibold">{data.me.score} pts</span>
              </div>
            )}
          </div>
          <div className="bg-[#121217] border border-[#FF4D00]/20 rounded-sm p-5 text-center" data-testid="ranking-upgrade">
            <p className="text-sm text-zinc-300">O <b>Ranking Geral completo</b> — com todos os profissionais e perfis — está disponível nos planos pagos.</p>
            {data?.upgrade_plan && <p className="text-sm text-[#00F0FF] mt-1">Disponível a partir do <b>Plano {data.upgrade_plan.name}</b> por <b>{data.upgrade_plan.price > 0 ? money(data.upgrade_plan.price) : "grátis"}</b>.</p>}
            <Button data-testid="ranking-upgrade-btn" onClick={() => navigate("/meu-plano")} className="mt-3 rounded-sm bg-[#FF4D00] hover:bg-[#E64500] gap-2"><Crown className="h-4 w-4" />{data?.upgrade_plan ? `Fazer upgrade para ${data.upgrade_plan.name}` : "Ver planos"}</Button>
          </div>
        </div>
      ) : (
      <div className="space-y-2">
        {users.map((u, i) => (
          <div key={u.id} data-testid={`rank-${u.id}`} className="flex items-center gap-4 bg-[#121217] border border-border rounded-sm p-4 hover:border-[#FF4D00]/30 transition-colors">
            <div className="w-8 text-center mono text-xl font-bold" style={{ color: medal(i) || "#71717A" }}>{i + 1}</div>
            <div onClick={() => navigate(`/musicos/${u.id}`)} className="h-12 w-12 rounded-sm bg-[#1C1C24] overflow-hidden flex items-center justify-center font-bold shrink-0 cursor-pointer">
              {u.photo ? <img src={u.photo} alt="" className="h-full w-full object-cover" /> : u.stage_name[0]}
            </div>
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/musicos/${u.id}`)}>
              <div className="font-semibold truncate">{u.stage_name}</div>
              <div className="text-sm text-zinc-500 truncate">{[u.city, u.state].filter(Boolean).join("/")} · {(u.instruments || []).slice(0,3).join(", ")}</div>
            </div>
            <div className="flex items-center gap-4 text-sm shrink-0">
              <span className="flex items-center gap-1 text-amber-400"><Star className="h-4 w-4 fill-amber-400" />{u.rating.avg || "—"}</span>
              <span className="flex items-center gap-1 text-zinc-400"><Briefcase className="h-4 w-4" />{u.gigs_done}</span>
              <span className="mono text-[#00F0FF] font-semibold">{u.score}</span>
              {u.id !== user?.id && <Button size="sm" data-testid={`invite-btn-${u.id}`} onClick={() => setInvitee(u)} className="rounded-sm bg-[#FF4D00] hover:bg-[#E64500] gap-1"><UserPlus className="h-4 w-4" />Convidar</Button>}
            </div>
          </div>
        ))}
      </div>
      )}

      <Dialog open={!!invitee} onOpenChange={(o) => !o && setInvitee(null)}>
        <DialogContent className="bg-[#121217] border-border">
          <DialogHeader><DialogTitle>Convidar {invitee?.stage_name}</DialogTitle></DialogHeader>
          {myEvents.length === 0 ? (
            <div className="text-sm text-zinc-400 py-4">Você não tem eventos abertos. <button onClick={() => { setInvitee(null); navigate("/eventos/novo"); }} className="text-[#00F0FF] hover:underline">Criar um evento</button> primeiro.</div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-zinc-500">Escolha um dos seus eventos abertos:</p>
              {myEvents.map((e) => (
                <button key={e.id} data-testid={`invite-to-${e.id}`} onClick={() => invite(e.id)}
                  className="w-full text-left bg-[#09090B] border border-border rounded-sm px-4 py-3 hover:border-[#FF4D00]/40 transition-colors">
                  <div className="font-medium">{e.name}</div>
                  <div className="text-sm text-zinc-500 mono">{formatDate(e.date)} · {e.start_time} · {e.confirmed_count}/{e.slots} vagas</div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
